import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {useNavigate} from 'react-router-dom';import {useTranslation} from 'react-i18next';import {MessageSquare, BookOpen, ChevronRight, Loader2, LogOut, Settings, Star, ArrowRight, Users, ScrollText, Sliders, Check} from 'lucide-react';import {API_URL as API} from '@/utils/apiConfig';import axios from 'axios';
import StudyPlanCard from '@/components/StudyPlanCard';
import TodayReadingCard from '@/components/TodayReadingCard';
import DidYouKnowHomeCard from '@/components/DidYouKnowHomeCard';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
import HomeLayoutEditor, { DEFAULT_HOME_LAYOUT } from '@/components/HomeLayoutEditor';
import AppStoreBadges from '@/components/AppStoreBadges';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * SortableCard — wraps one homescreen CTA card in a drag-sortable
 * container. Two mutually-exclusive interaction modes:
 *
 *   • **Normal**  — long-press handlers on the wrapper detect a
 *                    250-500ms hold to enter edit mode. Regular taps
 *                    fall through to the child button's onClick and
 *                    navigate as usual.
 *   • **Edit**    — dnd-kit drag listeners take over. The child button
 *                    is `disabled` + `pointer-events-none` so the
 *                    wrapper receives all pointer events cleanly.
 *                    A subtle wobble animation communicates the mode.
 *
 * Defined at module scope so it isn't re-created on every HomePage
 * render (which would break dnd-kit's identity tracking).
 */
function SortableCard({ id, index, editMode, justEnteredEditMode, fullWidth, longPressHandlers, children }) {
  // `disabled: !editMode` fully deactivates the sortable hook outside
  // edit mode. Without this, dnd-kit keeps its internal state and any
  // stale transform from a previously interrupted drag can linger,
  // leaving cards visibly shifted after "Done" is pressed.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !editMode,
  });

  // CRITICAL: the sortable `transform` (translate) and the wobble
  // `animation` (rotate) both write to the CSS `transform` property.
  // If they're on the same element, CSS animations override the
  // inline transform, which kills dnd-kit's snap-back on drop and
  // strands the card at the drag-release position — the user-
  // reported bug where cards don't return to normal after dragging
  // left. Fix: outer element handles sortable translate + transition
  // only; inner element handles wobble rotate + pickup lift. Two
  // elements, two property owners, no collision.
  const outerStyle = {
    transform: editMode ? CSS.Transform.toString(transform) : undefined,
    transition: editMode ? transition : undefined,
    zIndex: isDragging ? 20 : 'auto',
    opacity: isDragging ? 0.92 : 1,
    boxShadow: isDragging ? '0 20px 48px -12px rgba(0, 0, 0, 0.5)' : undefined,
    touchAction: editMode ? 'none' : undefined,
  };

  // Inner-element animation composition:
  //   • `home-card-pickup` fires ONCE when entering edit mode — a
  //     quick scale bounce that mimics iOS's pick-up haptic. Cascades
  //     across cards via a staggered delay for a satisfying cue.
  //   • `home-card-wobble` runs continuously in edit mode with the
  //     original per-card jitter delay.
  // Both live on the INNER wrapper only, so the OUTER sortable
  // transform is never overridden.
  let wobbleStyle;
  if (editMode && !isDragging) {
    const wobbleDelayMs = ((id.charCodeAt(0) * 17) % 400) - 200;
    if (justEnteredEditMode) {
      const pickupDelayMs = index * 40;
      wobbleStyle = {
        animation: `home-card-pickup 320ms ease-out ${pickupDelayMs}ms both, home-card-wobble 0.6s ease-in-out ${wobbleDelayMs}ms infinite`,
      };
    } else {
      wobbleStyle = {
        animation: `home-card-wobble 0.6s ease-in-out ${wobbleDelayMs}ms infinite`,
      };
    }
  }

  // In edit mode we intercept clicks at the CAPTURE phase so cards
  // whose inner button we don't control (e.g., DidYouKnowHomeCard)
  // still can't navigate away while the user is reordering. dnd-kit's
  // pointerdown listeners run BEFORE click, so drag detection is
  // unaffected — this only kills the tap-through.
  const blockClicks = editMode
    ? (e) => { e.stopPropagation(); e.preventDefault(); }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={outerStyle}
      className={`${editMode ? 'cursor-grab active:cursor-grabbing' : ''} ${fullWidth ? 'md:col-span-2' : ''}`}
      data-testid={`home-sortable-${id}`}
      onClickCapture={blockClicks}
      {...(editMode ? { ...attributes, ...listeners } : longPressHandlers)}
    >
      <div style={wobbleStyle}>
        {children}
      </div>
    </div>
  );
}

export default function HomePage({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Stale-while-revalidate: seed state from localStorage so navigating
  // back to home — OR closing and reopening the app — renders the
  // previously-fetched data INSTANTLY (no flash-of-loading, no pop-in
  // as sections populate one-by-one). A fresh fetch still runs in the
  // background and updates the UI silently. Cache is cleared on
  // login/logout via the `home_cache_` localStorage prefix so a new
  // user never sees the prior user's data.
  const [reflection, setReflection] = useState(() => {
    try { return JSON.parse(localStorage.getItem('home_cache_reflection') || 'null'); }
    catch { return null; }
  });
  const [loadingReflection, setLoadingReflection] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(() => {
    const n = parseInt(localStorage.getItem('home_cache_unread') || '0', 10);
    return Number.isFinite(n) ? n : 0;
  });

  // Homescreen layout — user-customizable order of the 5 primary CTA
  // cards. Seed from localStorage for instant render, then refresh
  // from /settings/home-layout in the background. `layoutOpen` drives
  // the bottom-sheet editor; `editMode` drives the inline "jiggle" +
  // drag experience triggered by long-pressing any card.
  const [homeLayout, setHomeLayout] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('home_cache_layout') || 'null');
      if (Array.isArray(cached) && cached.length > 0) return cached;
    } catch { /* ignore */ }
    return DEFAULT_HOME_LAYOUT;
  });
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  // Force-remount key for the DndContext. Every time the user exits
  // edit mode we bump this so dnd-kit's entire tree tears down and
  // rebuilds fresh — guarantees zero stale `transform` state can
  // survive across mode changes, no matter what interruption a drag
  // gesture suffered (touch escape, browser context menu, backend
  // network hiccup during saveLayout, iOS Safari swipe gesture, etc).
  const [dndSessionKey, setDndSessionKey] = useState(0);
  // Fires a one-shot cascade "pickup" bounce across all cards each
  // time the user enters edit mode. Clears after the animation window
  // so the ongoing wobble takes over cleanly.
  const [justEnteredEditMode, setJustEnteredEditMode] = useState(false);
  useEffect(() => {
    if (!editMode) {
      // Bump the dnd session key so the DndContext + SortableContext
      // remount from scratch — any lingering transforms on sibling
      // cards from an interrupted drag are wiped in one shot.
      setDndSessionKey((k) => k + 1);
      // Belt-and-suspenders: also force horizontal scroll back to 0.
      // On older iOS Safari (pre-16) `overflow-x: clip` is unsupported
      // and falls back to `visible`, so a mid-drag pointer overshoot
      // can leave the viewport horizontally scrolled. Explicit reset
      // guarantees we're back to the natural position on exit.
      try {
        window.scrollTo({ left: 0, top: window.scrollY, behavior: 'instant' });
        document.documentElement.scrollLeft = 0;
        document.body.scrollLeft = 0;
      } catch { /* older browsers reject 'instant' — swallow */ }
      return;
    }
    setJustEnteredEditMode(true);
    const t = setTimeout(() => setJustEnteredEditMode(false), 600);
    return () => clearTimeout(t);
  }, [editMode]);
  const isGuest = !!user?.is_guest;
  const { enabled: studyPlansEnabled, loaded: flagsLoaded } = useFeatureFlag('study_plans');
  const { enabled: communityEnabled } = useFeatureFlag('community');
  const { enabled: weeklyRecapEnabled } = useFeatureFlag('weekly_recap');

  // Greeting name — prefer the first name, then fall back to a
  // translated "Guest" for anonymous sessions (backend returns the
  // literal string "Guest") and "there" as the last resort so the
  // greeting always reads naturally in every locale.
  const rawName = user?.name?.split(' ')[0];
  const greetingName = (!rawName || rawName === 'Guest')
    ? (user?.is_guest ? t('home.guest_name', 'Guest') : t('home.default_name', 'there'))
    : rawName;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('home.good_morning');
    if (hour < 17) return t('home.good_afternoon');
    return t('home.good_evening');
  };

  useEffect(() => {
    // Fetch reflection in background — don't block page interaction.
    // We only show the loading spinner on the FIRST mount (no cache);
    // subsequent visits keep the prior reflection visible during refresh.
    const controller = new AbortController();
    if (!reflection) setLoadingReflection(true);
    axios.get(`${API}/reflections/today`, { withCredentials: true, signal: controller.signal })
      .then(res => {
        setReflection(res.data);
        try { localStorage.setItem('home_cache_reflection', JSON.stringify(res.data)); } catch { /* quota */ }
      })
      .catch(() => {})
      .finally(() => setLoadingReflection(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch unread community notification count
  useEffect(() => {
    if (isGuest) return;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/community/notifications/unread-count`, { withCredentials: true });
        const count = res.data.count || 0;
        setUnreadNotifs(count);
        try { localStorage.setItem('home_cache_unread', String(count)); } catch { /* quota */ }
      } catch { /* silent */ }
    };
    fetchUnread();
  }, [isGuest]);

  // Despia push permissions are handled automatically by the native wrapper.
  // No need to prompt — iOS asks on first push delivery.

  // Load home layout from server (stale-while-revalidate). Guest users
  // still get the default order — the endpoint requires auth so we
  // skip the fetch for them and rely on localStorage.
  useEffect(() => {
    if (isGuest) return;
    let cancelled = false;
    axios.get(`${API}/settings/home-layout`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const blocks = Array.isArray(res.data?.blocks) ? res.data.blocks : null;
        if (blocks && blocks.length > 0) {
          setHomeLayout(blocks);
          try { localStorage.setItem('home_cache_layout', JSON.stringify(blocks)); }
          catch { /* quota */ }
        }
      })
      .catch(() => { /* fall back to default order silently */ });
    return () => { cancelled = true; };
  }, [isGuest]);

  // Persist a new layout to the server. Optimistically updates local
  // state + cache so the UI stays snappy even on slow networks;
  // rejects the promise on failure so the editor's toast reports it.
  const saveLayout = useCallback(async (next) => {
    setHomeLayout(next);
    try { localStorage.setItem('home_cache_layout', JSON.stringify(next)); }
    catch { /* quota */ }
    if (isGuest) return; // Guests can still tweak locally but nothing to sync
    await axios.put(
      `${API}/settings/home-layout`,
      { blocks: next },
      { withCredentials: true }
    );
  }, [isGuest]);

  // Sensors for INLINE (jiggle-mode) drag-and-drop. Distinct from the
  // editor sheet's sensors so the desktop constraint can be tighter
  // (immediate drag) while touch requires a 250ms hold — long enough
  // to distinguish a drag from a tap that navigates.
  const inlineSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 10 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleInlineDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setHomeLayout((prev) => {
      const oldIdx = prev.indexOf(active.id);
      const newIdx = prev.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      const next = arrayMove(prev, oldIdx, newIdx);
      // Fire-and-forget save; UI has already updated. Any failure is
      // swallowed (a stale layout is fine — user can re-drag).
      saveLayout(next).catch(() => {});
      return next;
    });
  }, [saveLayout]);

  // Long-press detection wired to each reorderable card. On touch we
  // arm a 500ms timer; releasing before it fires means a normal tap
  // (navigation happens). Any pointer move beyond 8px cancels the
  // timer so scrolling is unaffected.
  const longPressTimer = useRef(null);
  const longPressStart = useRef({ x: 0, y: 0 });
  const armLongPress = useCallback((e) => {
    if (editMode) return; // already in edit mode
    const point = e.touches?.[0] || e;
    longPressStart.current = { x: point.clientX ?? 0, y: point.clientY ?? 0 };
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      setEditMode(true);
      // Haptic feedback on supporting devices (iOS via native SDK,
      // Android via vibration API). Best-effort — silent failure.
      try { navigator.vibrate?.(10); } catch { /* ignore */ }
    }, 500);
  }, [editMode]);
  const cancelLongPress = useCallback(() => {
    clearTimeout(longPressTimer.current);
  }, []);
  const moveLongPress = useCallback((e) => {
    const point = e.touches?.[0] || e;
    const dx = Math.abs((point.clientX ?? 0) - longPressStart.current.x);
    const dy = Math.abs((point.clientY ?? 0) - longPressStart.current.y);
    if (dx > 8 || dy > 8) clearTimeout(longPressTimer.current);
  }, []);
  useEffect(() => () => clearTimeout(longPressTimer.current), []);

  const longPressHandlers = useMemo(() => ({
    onTouchStart: armLongPress,
    onTouchEnd: cancelLongPress,
    onTouchCancel: cancelLongPress,
    onTouchMove: moveLongPress,
    onMouseDown: armLongPress,
    onMouseUp: cancelLongPress,
    onMouseLeave: cancelLongPress,
    onMouseMove: moveLongPress,
  }), [armLongPress, cancelLongPress, moveLongPress]);

  // Card visibility rules — mirror the previous conditional-render
  // logic so guest and study-plans-disabled users see the same subset
  // they always did (just in the user's preferred order). DYK self-
  // hides when there's no published fact (the component returns null),
  // so we don't need extra gating here.
  const cardVisibility = useMemo(() => ({
    'did-you-know': true,
    chat: true,
    reflection: true,
    'study-plans': !isGuest && studyPlansEnabled,
    community: !isGuest && communityEnabled,
    reviews: true,
  }), [isGuest, studyPlansEnabled, communityEnabled]);

  // Applied render order = user's saved order, filtered by visibility.
  // Also drop any unknown ids defensively (shouldn't happen given the
  // server-side merge but protects against stale cached layouts).
  const visibleLayout = useMemo(
    () => homeLayout.filter((id) => cardVisibility[id]),
    [homeLayout, cardVisibility],
  );

  // Central navigation helper — swallows clicks in edit mode so the
  // card wobble doesn't accidentally navigate away when the user
  // meant to reorder. All 5 CTA buttons route through this.
  const goto = useCallback((path) => {
    if (editMode) return;
    navigate(path);
  }, [editMode, navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch {
      navigate('/login');
    }
  };

  return (
    <div
      className="page-container relative overflow-x-hidden"
      style={{ background: 'var(--theme-background)' }}
      data-testid="home-page"
    >
      {/* Radial gradient glow — matches landing page */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, var(--theme-accent) 0%, transparent 70%)' }}
      />
      {/* Header */}
      <header
        className="relative z-10 px-5 md:px-8 py-4 flex items-center justify-between"
        style={{
          paddingLeft: 'max(1.25rem, var(--sal))',
          paddingRight: 'max(1.25rem, var(--sar))',
          // Reserve room for the OS status bar on every platform that
          // injects a safe-area top inset (Despia iOS + Android, iOS
          // PWA, Android display-cutout devices). Falls back to the
          // existing `py-4` height when no inset is present so the
          // desktop / web layout is unchanged.
          paddingTop: '1rem',
        }}
      >
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt={t('generated.homepage_eili_2db3')} className="h-8 w-8 object-contain" />
          <span className="text-xl font-bold" style={{ fontFamily: "'Playwrite ES Deco', cursive", color: 'var(--theme-text)' }}>{t('generated.homepage_eili_2db3')}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate('/bible')}
            className="p-2 rounded-full transition-all"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
            data-testid="home-bible-reader-btn"
          >
            <BookOpen className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-full transition-all"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
            data-testid="home-settings-btn"
          >
            <Settings className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
          {!isGuest && (
            <button
              onClick={() => setLayoutOpen(true)}
              className="p-2 rounded-full transition-all"
              style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
              data-testid="home-customize-btn"
              aria-label={t('generated.homepage_customize_homescreen_d2d2')}
              title={t('generated.homepage_customize_homescreen_d2d2')}
            >
              <Sliders className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          )}
          {isGuest ? (
            <>
              {/* Exit guest session — ends the anonymous session on
                  the server and returns the visitor to the marketing
                  landing so there's a clear "back out" affordance
                  for anyone who chose Explore-as-guest and now wants
                  to leave. Hidden inside the Despia native wrapper
                  because native's equivalent home is the onboarding
                  carousel, not the marketing site. */}
              {typeof navigator !== 'undefined' && !navigator.userAgent.toLowerCase().includes('despia') && (
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
                    } catch { /* ignore — still navigate */ }
                    window.location.replace('/');
                  }}
                  className="p-2 rounded-full transition-all"
                  style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
                  data-testid="home-guest-exit-btn"
                  aria-label={t('home.exit_guest', 'Exit to home')}
                  title={t('home.exit_guest', 'Exit to home')}
                >
                  <LogOut className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
                </button>
              )}
              <button
                onClick={() => navigate('/login')}
                className="ml-1 px-4 py-2 rounded-full text-sm font-medium transition-all"
                style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--theme-accent)' }}
                data-testid="home-signin-btn"
              >
                {t('home.sign_in')}
              </button>
            </>
          ) : (
            <button
              onClick={handleLogout}
              className="p-2 rounded-full transition-all"
              style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
              data-testid="home-logout-btn"
            >
              <LogOut className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          )}
        </div>
      </header>
      {/* Main Content */}
      <main className="page-content relative z-10 px-5 md:px-8 py-6 md:py-10 pb-24 max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto w-full">
        {/* Greeting */}
        <div className="text-center mb-10" data-testid="home-greeting">
          <h1
            className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight"
            style={{ color: 'var(--theme-text)' }}
          >
            {getGreeting()}, {greetingName}
          </h1>
          <p className="text-base max-w-md mx-auto leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('home.explore_prompt')}
          </p>
        </div>

        {/* Today's reading at a glance — hides itself if user has no active plans.
            When the user has multiple active plans, this card becomes a
            horizontal swipeable carousel with pagination dots, so all in-flight
            plans are reachable from one home-screen surface without a
            secondary "Continue your plan" rail. */}
        <TodayReadingCard disabled={isGuest || (flagsLoaded && !studyPlansEnabled)} />

        {/* ── Reorderable CTA zone ─────────────────────────────────────
            All 5 primary cards live inside one sortable grid so users
            can reorder them freely. Long-pressing any card enters
            "edit mode" (iOS-style jiggle) where the cards become
            draggable in place. Tapping "Done" (or pressing Escape)
            exits edit mode and persists the order to the server. */}
        <DndContext
          key={dndSessionKey}
          sensors={inlineSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleInlineDragEnd}
        >
          <SortableContext items={visibleLayout} strategy={rectSortingStrategy}>
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-6"
              data-testid="home-cta-grid"
              aria-label={editMode ? 'Reorder homescreen cards' : undefined}
            >
              {visibleLayout.map((id, idx) => (
                <SortableCard
                  key={id}
                  id={id}
                  index={idx}
                  editMode={editMode}
                  justEnteredEditMode={justEnteredEditMode}
                  fullWidth={id === 'did-you-know'}
                  longPressHandlers={longPressHandlers}
                >
                  {id === 'did-you-know' && (
                    // DYK is the only card that self-hides when there's
                    // no published fact. It spans the full grid width
                    // (via fullWidth prop → md:col-span-2) so its
                    // pulsing "NEW" frame stays visually prominent.
                    // The DidYouKnowHomeCard component has its own
                    // navigate('/did-you-know') click handler; the
                    // SortableCard's onClickCapture blocks it in
                    // editMode so the user can drag without accidental
                    // navigation.
                    (<DidYouKnowHomeCard />)
                  )}

                  {id === 'chat' && (
                    <button
                      onClick={() => goto('/chat')}
                      disabled={editMode}
                      className={`w-full text-left rounded-2xl p-6 transition-all group ${editMode ? 'pointer-events-none' : 'hover:scale-[1.02] active:scale-[0.98]'}`}
                      style={{
                        background: 'var(--glass-bg)',
                        border: '1px solid var(--glass-border)',
                        backdropFilter: 'blur(12px)',
                        opacity: 1,
                      }}
                      data-testid="home-chat-card"
                    >
                      <div className="p-3 rounded-xl inline-flex mb-4" style={{ background: 'var(--glass-bg-active)' }}>
                        <MessageSquare className="h-6 w-6" style={{ color: 'var(--theme-accent)' }} />
                      </div>
                      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
                        {t('home.chat_title')}
                      </h2>
                      <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
                        {t('home.chat_desc')}
                      </p>
                      <div className="flex items-center gap-1.5 text-sm font-medium transition-all group-hover:gap-2.5" style={{ color: 'var(--theme-accent)' }}>
                        {t('home.chat_cta')}
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </button>
                  )}

                  {id === 'reflection' && (
                    <button
                      onClick={() => goto('/reflection')}
                      disabled={editMode}
                      className={`w-full text-left rounded-2xl p-6 transition-all group ${editMode ? 'pointer-events-none' : 'hover:scale-[1.02] active:scale-[0.98]'}`}
                      style={{
                        background: 'var(--glass-bg)',
                        border: '1px solid var(--glass-border)',
                        backdropFilter: 'blur(12px)',
                        opacity: 1,
                      }}
                      data-testid="home-reflection-card"
                    >
                      <div className="p-3 rounded-xl inline-flex mb-4" style={{ background: 'var(--glass-bg-active)' }}>
                        <BookOpen className="h-6 w-6" style={{ color: 'var(--theme-accent)' }} />
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
                          {t('home.reflection_title')}
                        </h2>
                        <span
                          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium"
                          style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
                        >
                          {t('home.reflection_today')}
                        </span>
                      </div>
                      {loadingReflection ? (
                        <div className="flex items-center gap-2 py-1">
                          <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-textSecondary)' }} />
                          <span className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>{t('home.reflection_loading')}</span>
                        </div>
                      ) : reflection ? (
                        <div>
                          <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
                            {reflection.topic}
                          </p>
                          <p className="text-sm leading-relaxed line-clamp-2 mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
                            {reflection.introduction?.replace(/\*\*/g, '')}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
                          {t('home.reflection_empty')}
                        </p>
                      )}
                      <div className="flex items-center gap-1.5 text-sm font-medium transition-all group-hover:gap-2.5" style={{ color: 'var(--theme-accent)' }}>
                        {t('home.reflection_cta')}
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </button>
                  )}

                  {id === 'study-plans' && (
                    <button
                      onClick={() => goto('/study-plans')}
                      disabled={editMode}
                      className={`w-full text-left rounded-2xl p-5 transition-all group ${editMode ? 'pointer-events-none' : 'hover:scale-[1.01] active:scale-[0.99]'}`}
                      style={{
                        background: 'var(--glass-bg)',
                        border: '1px solid var(--glass-border)',
                        backdropFilter: 'blur(12px)',
                        opacity: 1,
                      }}
                      data-testid="home-study-plans-card"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2.5 rounded-xl" style={{ background: 'var(--glass-bg-active)' }}>
                            <ScrollText className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                              {t('generated.homepage_study_plans_337f')}
                            </h3>
                            <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                              {t('generated.homepage_guided_journeys_through_3119')}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" style={{ color: 'var(--theme-textSecondary)' }} />
                      </div>
                    </button>
                  )}

                  {id === 'community' && (
                    <button
                      onClick={() => goto('/community')}
                      disabled={editMode}
                      className={`w-full text-left rounded-2xl p-5 transition-all group ${editMode ? 'pointer-events-none' : 'hover:scale-[1.01] active:scale-[0.99]'}`}
                      style={{
                        background: 'var(--glass-bg)',
                        border: '1px solid var(--glass-border)',
                        backdropFilter: 'blur(12px)',
                        opacity: 1,
                      }}
                      data-testid="home-community-card"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2.5 rounded-xl relative" style={{ background: 'var(--glass-bg-active)' }}>
                            <Users className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
                            {unreadNotifs > 0 && (
                              <span
                                className="absolute -top-1 -right-1 h-3 w-3 rounded-full border-2"
                                style={{ background: '#ef4444', borderColor: 'var(--glass-bg)' }}
                                data-testid="community-unread-dot"
                              />
                            )}
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                              {t('home.community_title')}
                            </h3>
                            <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                              {t('home.community_desc')}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" style={{ color: 'var(--theme-textSecondary)' }} />
                      </div>
                    </button>
                  )}

                  {id === 'reviews' && (
                    <button
                      onClick={() => goto('/reviews')}
                      disabled={editMode}
                      className={`w-full text-left rounded-2xl p-5 transition-all group ${editMode ? 'pointer-events-none' : 'hover:scale-[1.01] active:scale-[0.99]'}`}
                      style={{
                        background: 'var(--glass-bg)',
                        border: '1px solid var(--glass-border)',
                        backdropFilter: 'blur(12px)',
                        opacity: 1,
                      }}
                      data-testid="home-reviews-card"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2.5 rounded-xl" style={{ background: 'var(--glass-bg-active)' }}>
                            <Star className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                              {t('home.reviews_title')}
                            </h3>
                            <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                              {t('home.reviews_desc')}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" style={{ color: 'var(--theme-textSecondary)' }} />
                      </div>
                    </button>
                  )}
                </SortableCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* App-store badges — hides itself entirely for Despia native
            users (the AppStoreBadges component checks the UA). On web
            visitors (desktop + mobile browsers), presents both stores
            so users can install on their phone whenever they're ready. */}
        <AppStoreBadges source="home-badges" className="mt-2 mb-6" />

        {/* Verse filler — glass treatment */}
        <div
          className="mt-10 text-center px-6 py-7 rounded-2xl"
          style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            backdropFilter: 'blur(12px)',
          }}
          data-testid="home-verse-filler"
        >
          <p
            className="text-sm italic leading-relaxed mb-2"
            style={{ color: 'var(--theme-textSecondary)' }}
          >
            {t('home.verse_text')}
          </p>
          <p
            className="text-xs font-medium"
            style={{ color: 'var(--theme-accent)' }}
          >
            {t('home.verse_ref')}
          </p>
        </div>
      </main>
      {/* Done bar — appears while in inline edit mode. Tapping "Done"
          exits the jiggle mode; auto-save fires as each drag settles
          via `handleInlineDragEnd`, so this button is purely a mode
          exit, not a save trigger. */}
      {editMode && (
        <div
          className="fixed left-0 right-0 z-40 flex justify-center pointer-events-none"
          style={{
            bottom: 'max(1rem, calc(1rem + var(--sab, 0px)))',
          }}
          data-testid="home-edit-done-bar"
        >
          <button
            onClick={() => setEditMode(false)}
            className="pointer-events-auto flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm shadow-lg"
            style={{
              background: 'var(--theme-accent)',
              color: 'var(--theme-accent-contrast, #0f172a)',
            }}
            data-testid="home-edit-done-btn"
          >
            <Check className="h-4 w-4" />
            {t('generated.homepage_done_163a')}
          </button>
        </div>
      )}
      {/* Bottom-sheet editor — opened from the Sliders icon in the
          header. Handles reorder via drag on all sensor types
          (pointer/touch/keyboard). */}
      <HomeLayoutEditor
        open={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        blocks={homeLayout}
        onSave={saveLayout}
      />
      {/* Keyframes for the iOS-style card wobble applied by
          SortableCard when editMode is on. Injected once at the
          component root so we don't pollute the global stylesheet. */}
      <style>{`
        @keyframes home-card-wobble {
          0%   { transform: rotate(-0.6deg) translateY(0); }
          25%  { transform: rotate(0.6deg) translateY(-1px); }
          50%  { transform: rotate(-0.4deg) translateY(0); }
          75%  { transform: rotate(0.4deg) translateY(1px); }
          100% { transform: rotate(-0.6deg) translateY(0); }
        }
        /* One-shot "pickup" bounce fired when the user enters edit
           mode. A subtle scale lift (1 → 1.04 → 1) with a soft drop
           shadow gives a haptic-like cue that cards became draggable.
           Applied to the INNER wrapper only so it never collides with
           dnd-kit's outer transform. */
        @keyframes home-card-pickup {
          0%   { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
          50%  { transform: scale(1.04); filter: drop-shadow(0 10px 18px rgba(0,0,0,0.28)); }
          100% { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes home-card-wobble {
            0%, 100% { transform: none; }
          }
          @keyframes home-card-pickup {
            0%, 100% { transform: none; filter: none; }
          }
        }
      `}</style>
    </div>
  );
}
